import { useState } from 'react';
import { faqs } from '../../../mocks/faqs';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

export default function FAQSection() {
  const [openId, setOpenId] = useState<number | null>(1);

  return (
    <section className="bg-brand-cream py-24 relative overflow-hidden">
      {/* Background decorative */}
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-brand-green/5 rounded-full translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
          {/* Left: header */}
          <RevealOnScroll direction="left" className="lg:col-span-2">
            <span className="text-brand-green text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-4">
              <span className="w-8 h-px bg-brand-green inline-block" />
              FAQ
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-brand-dark mb-5 leading-tight">
              Common Questions<br />
              <span className="text-brand-green italic">About Roofing</span><br />
              in Chicago
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              Everything you need to know before starting your roofing or exterior construction project.
              Can&apos;t find your answer? Call us directly — same response, zero pressure.
            </p>

            {/* Phone CTA */}
            <a
              href="tel:7086984072"
              className="inline-flex items-center gap-3 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-bold px-6 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-white/15 rounded-lg">
                <i className="ri-phone-fill text-brand-gold text-sm" />
              </div>
              <div>
                <div className="text-xs text-white/60">Call Us Directly</div>
                <div>(708) 698-4072</div>
              </div>
            </a>

            {/* Category quick links */}
            <div className="mt-8 flex flex-wrap gap-2">
              {[
                { label: 'Cost Questions', icon: 'ri-money-dollar-circle-line' },
                { label: 'Emergency Repairs', icon: 'ri-alarm-warning-line' },
                { label: 'Service Areas', icon: 'ri-map-pin-2-line' },
              ].map((tag) => (
                <span key={tag.label} className="inline-flex items-center gap-1.5 bg-white border border-brand-cream-3 text-gray-600 text-xs px-3 py-1.5 rounded-full">
                  <i className={`${tag.icon} text-brand-green text-xs`} />
                  {tag.label}
                </span>
              ))}
            </div>
          </RevealOnScroll>

          {/* Right: accordion */}
          <RevealOnScroll direction="right" delay={100} className="lg:col-span-3 flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <div
                key={faq.id}
                className={`rounded-xl border overflow-hidden transition-all duration-200 ${
                  openId === faq.id
                    ? 'border-brand-green/30 bg-white'
                    : 'border-brand-cream-3 bg-white hover:border-brand-green/20'
                }`}
              >
                <button
                  className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                  onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-bold text-brand-gold/60 mt-0.5 w-5 flex-shrink-0">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-sm font-bold text-brand-dark leading-snug flex-1">{faq.question}</h3>
                  </div>
                  <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full transition-colors mt-0.5 ${openId === faq.id ? 'bg-brand-green text-white' : 'bg-brand-cream text-brand-green'}`}>
                    <i className={`text-sm ${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>
                {openId === faq.id && (
                  <div className="px-6 pb-6 border-t border-brand-cream-2">
                    <div className="pt-4 text-sm text-gray-600 leading-relaxed pl-8">{faq.answer}</div>
                  </div>
                )}
              </div>
            ))}
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}
